import { AfterViewInit, Component, ElementRef, Input, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbActiveModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { EmptyError, lastValueFrom, Subject, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { parseFlatpickrDateTime } from '../../../../../../_shared/helpers/date-picker/date-picker.helper';
import { IncidentSeverity, IncidentSeverityLabelKeys } from '../../../enums/incident-severity.enum';
import { ReportIncidentStep } from '../../../enums/report-incident-step.enum';
import { AssetDropdownItem, AssetSearchDropdownComponent } from '../../../../../_shared/components/asset-search-dropdown/asset-search-dropdown.component';
import { AccidentsAndIncidentsService } from '../../../services/accidents-and-incidents.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { LookupIncidentType } from '../../../../../_shared/models/lookup.model';
import { IncidentAttachmentUpload } from '../../../models/accidents-and-incidents-request.model';
import {
  areUploadsComplete,
  buildTempAttachmentFormData,
  createAttachmentUpload,
  getMediaType,
  isDuplicateAttachment,
  markUploadFailed,
  markUploadSucceeded,
  toAttachmentRequests,
  trackUploadProgress
} from '../../../helpers/incident-attachment-upload.helper';

@Component({
  selector: 'app-report-incident-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgbDropdownModule,
    FlatpickrDatePickerComponent,
    AssetSearchDropdownComponent,
    TranslocoDirective,
    TranslocoPipe
  ],
  templateUrl: './report-incident-modal.component.html',
  styleUrl: './report-incident-modal.component.scss'
})
export class ReportIncidentModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() accountIds: number[] = [];
  @ViewChild('locationInput') locationInput?: ElementRef<HTMLInputElement>;

  private activeModal: NgbActiveModal = inject(NgbActiveModal);
  private fb: FormBuilder = inject(FormBuilder);
  private ngZone: NgZone = inject(NgZone);
  private accidentsAndIncidentsService = inject(AccidentsAndIncidentsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private toastr: ToastrService = inject(ToastrService);
  private lookupService = inject(LookupService);
  private readonly transloco: TranslocoService = inject(TranslocoService);

  readonly reportIncidentStep = ReportIncidentStep;
  readonly severities: IncidentSeverity[] = [IncidentSeverity.LowImpact, IncidentSeverity.MediumImpact, IncidentSeverity.HighImpact];
  readonly severityLabelKeys = IncidentSeverityLabelKeys;
  readonly maxAttachmentCount = 5;
  readonly maxVideoAttachmentSizeBytes = 150 * 1024 * 1024;

  reportForm: FormGroup;
  currentStep: ReportIncidentStep = ReportIncidentStep.BasicInformation;
  incidentTypes: LookupIncidentType[] = [];
  selectedAsset: AssetDropdownItem | null = null;
  selectedIncidentType: LookupIncidentType | null = null;
  selectedSeverity: IncidentSeverity | null = null;
  dateTime: string = '';
  latitude: number | null = null;
  longitude: number | null = null;
  isLoadingIncidentTypes: boolean = false;
  isSubmitting: boolean = false;
  witnessDetails: string = '';
  attachedFiles: IncidentAttachmentUpload[] = [];
  isDragOver: boolean = false;
  attachmentError: string = '';
  private autocomplete: google.maps.places.Autocomplete | null = null;
  private locationSubscription?: Subscription;
  private selectedLocationValue: string = '';
  private cancelSubject = new Subject<void>();

  constructor() {
    this.reportForm = this.fb.group({
      assetId: ['', Validators.required],
      location: ['', Validators.required],
      description: ['', Validators.required],
      driverInvolved: ['', Validators.required]
    });
  }

  get incidentTypeLabel(): string {
    return this.selectedIncidentType?.name
      ? (this.selectedIncidentType.localizedName || this.selectedIncidentType.name)
      : this.transloco.translate('accidents.modals.reportIncident.selectIncidentType');
  }

  get severityLabel(): string {
    return this.transloco.translate(
      this.selectedSeverity ? this.severityLabelKeys[this.selectedSeverity] : 'accidents.modals.reportIncident.selectSeverity'
    );
  }

  get isStep1Valid(): boolean {
    return !!(
      this.reportForm.get('assetId')?.value &&
      this.selectedIncidentType &&
      this.selectedSeverity &&
      this.dateTime &&
      this.reportForm.get('location')?.value &&
      this.latitude !== null &&
      this.longitude !== null &&
      this.reportForm.get('description')?.value
    );
  }

  get isStep2Valid(): boolean {
    return !!this.reportForm.get('driverInvolved')?.value?.trim() && this.areAttachmentsReady;
  }

  get areAttachmentsReady(): boolean {
    return areUploadsComplete(this.attachedFiles);
  }

  async ngOnInit(): Promise<void> {
    this.locationSubscription = this.reportForm.get('location')?.valueChanges.subscribe((value: string) => {
      if (value !== this.selectedLocationValue) {
        this.latitude = null;
        this.longitude = null;
      }
    });

    await this.loadIncidentTypes();
  }

  ngAfterViewInit(): void {
    void this.initAutocomplete();
  }

  ngOnDestroy(): void {
    this.locationSubscription?.unsubscribe();

    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }

    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  onAssetSelect(asset: AssetDropdownItem): void {
    if (this.selectedAsset && this.selectedAsset.assetId !== asset.assetId) {
      this.attachedFiles = [];
      this.attachmentError = '';
    }

    this.selectedAsset = asset;
    this.reportForm.get('assetId')?.setValue(asset.assetId);
    this.reportForm.get('assetId')?.markAsTouched();
  }

  onIncidentTypeSelect(type: LookupIncidentType): void {
    this.selectedIncidentType = type;
  }

  onSeveritySelect(severity: IncidentSeverity): void {
    this.selectedSeverity = severity;
  }

  nextStep(): void {
    if (this.currentStep === ReportIncidentStep.BasicInformation && this.isStep1Valid) {
      this.currentStep = ReportIncidentStep.PeopleInvolved;
    }
  }

  previousStep(): void {
    if (this.currentStep === ReportIncidentStep.PeopleInvolved) {
      this.currentStep = ReportIncidentStep.BasicInformation;
    }
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  async submit(): Promise<void> {
    const occurredAt = parseFlatpickrDateTime(this.dateTime);
    if (!this.isStep1Valid || !this.isStep2Valid || !this.selectedAsset || !this.selectedIncidentType || !this.selectedSeverity || this.latitude === null || this.longitude === null || !occurredAt) {
      return;
    }

    const driverInvolved = this.reportForm.get('driverInvolved')?.value?.trim();

    this.isSubmitting = true;

    try {
      await this.accidentsAndIncidentsService.upsertIncident({
        incidentId: null,
        assetId: this.selectedAsset.assetId,
        incidentType: this.selectedIncidentType.id,
        severityType: this.selectedSeverity,
        driverInvolved,
        locationText: this.reportForm.get('location')?.value,
        description: this.reportForm.get('description')?.value || null,
        witnessDetails: this.witnessDetails || null,
        latitude: this.latitude,
        longitude: this.longitude,
        occurredAt: occurredAt.toISOString(),
        attachments: toAttachmentRequests(this.attachedFiles)
      }, this.cancelSubject);

      this.activeModal.close(true);
    } catch (error) {
      this.isSubmitting = false;
      this.errorHandlerService.handleError(error, {
        showToast: false,
        callback: () => this.toastr.error(this.transloco.translate('accidents.modals.reportIncident.errors.submitFailed'))
      });
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.addFiles(files);
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.addFiles(input.files);
      input.value = '';
    }
  }

  removeFile(index: number): void {
    this.attachedFiles.splice(index, 1);
    this.attachmentError = '';
  }

  private async loadIncidentTypes(): Promise<void> {
    this.isLoadingIncidentTypes = true;

    try {
      const response = await this.lookupService.getIncidentTypesAsync(this.cancelSubject);
      this.incidentTypes = response.data ?? [];
      this.isLoadingIncidentTypes = false;
    } catch (error) {
      this.incidentTypes = [];
      this.isLoadingIncidentTypes = false;
      this.errorHandlerService.handleError(error, { showToast: false });
    }
  }

  private async initAutocomplete(): Promise<void> {
    try {
      await google.maps.importLibrary('places');
    } catch {
      console.warn('Google Places library could not be loaded');
      return;
    }

    if (!this.locationInput?.nativeElement) return;

    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
    }

    this.autocomplete = new google.maps.places.Autocomplete(
      this.locationInput.nativeElement,
      {
        types: ['establishment', 'geocode'],
        fields: ['formatted_address', 'geometry', 'name']
      }
    );

    this.autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = this.autocomplete?.getPlace();
        if (!place?.geometry?.location) return;

        const location = place.formatted_address || place.name || '';
        this.selectedLocationValue = location;
        this.latitude = place.geometry.location.lat();
        this.longitude = place.geometry.location.lng();
        this.reportForm.get('location')?.setValue(location);
        this.reportForm.get('location')?.markAsDirty();
      });
    });
  }

  private addFiles(files: FileList): void {
    // Files can only be added on step 2, which isn't reachable without an asset
    const assetId = this.selectedAsset?.assetId;
    if (!assetId) {
      return;
    }

    for (const file of Array.from(files)) {
      if (!this.canAddFile(file)) {
        continue;
      }

      const attachment = createAttachmentUpload(file);
      this.attachedFiles.push(attachment);
      void this.uploadTempAttachment(attachment, assetId);
    }
  }

  private canAddFile(file: File): boolean {
    this.attachmentError = '';

    if (this.attachedFiles.length >= this.maxAttachmentCount) {
      this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.maxAttachments', { count: this.maxAttachmentCount });
      return false;
    }

    if (!this.isSupportedMedia(file)) {
      this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.unsupportedMedia');
      return false;
    }

    if (this.isVideo(file) && file.size > this.maxVideoAttachmentSizeBytes) {
      this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.videoTooLarge');
      return false;
    }

    if (isDuplicateAttachment(this.attachedFiles, file)) {
      this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.duplicateFile');
      return false;
    }

    return true;
  }

  private async uploadTempAttachment(attachment: IncidentAttachmentUpload, assetId: number): Promise<void> {
    try {
      await lastValueFrom(
        this.accidentsAndIncidentsService
          .uploadTempIncidentAttachment(buildTempAttachmentFormData(assetId, attachment), this.cancelSubject)
          .pipe(trackUploadProgress(attachment))
      );

      markUploadSucceeded(attachment);
    } catch (error) {
      // Upload was cancelled because the modal closed, so there's nothing to report
      if (error instanceof EmptyError) {
        return;
      }

      markUploadFailed(attachment);
      this.attachmentError = this.transloco.translate('accidents.modals.reportIncident.errors.uploadFailed');
      this.errorHandlerService.handleError(error, { showToast: true });
    }
  }

  private isSupportedMedia(file: File): boolean {
    const mediaType = getMediaType(file);
    return mediaType.startsWith('image/') || mediaType.startsWith('video/');
  }

  private isVideo(file: File): boolean {
    return getMediaType(file).startsWith('video/');
  }
}
